// ═══════════════════════════════════════════════════════════════════
//  DocOverlay — atlas-document rectangles on the Minimap.
// ═══════════════════════════════════════════════════════════════════
//
//  Phase B of MINIMAP_ATLAS_PLAN. Replaces the generic group rectangle
//  for any BlockGroup whose atlasRef is set, drawing a doc-icon badge,
//  purple stroke, and a truncated title label. Single-click centres
//  the minimap (handled by parent), double-click drills into the doc.
//
// ═══════════════════════════════════════════════════════════════════

import { useMemo } from 'react';
import { BookOpen } from 'lucide-react';
import type { BlockGroup, WorkflowBlock, BlockDimensions } from '@/types/workflow';
import type { Atlas } from '@/types/atlas';

const TITLE_MAX_CHARS = 12;

export interface DocOverlayProps {
  /** All BlockGroups on the active sheet — internally filtered to atlasRef !== undefined. */
  groups: BlockGroup[];
  blocks: WorkflowBlock[];
  blockDimensions?: Record<string, BlockDimensions>;
  /** Maps a canvas X coordinate to its minimap X position. */
  toScreenX: (canvasX: number) => number;
  toScreenY: (canvasY: number) => number;
  toScreenSize: (canvasSize: number) => number;
  /** Pixels-per-screen-coord scale factor used by the rest of the minimap. */
  scale: number;
  /** Atlas manifest — looked up to fetch doc title + sheet count. */
  atlas?: Atlas | null;
  /** Document id of the workflow currently being viewed (for "you are here"). */
  currentDocId?: string;
  /** Double-click handler: drill into the doc. */
  onDrillDownDoc?: (documentId: string, sheetId?: string) => void;
}

interface DocRect {
  group: BlockGroup;
  documentId: string;
  initialSheetId?: string;
  title: string;
  sheetCount: number;
  blockCount: number;
  isCurrent: boolean;
  /** Minimap-pixel bounds. */
  left: number;
  top: number;
  width: number;
  height: number;
}

export function DocOverlay({
  groups,
  blocks,
  blockDimensions,
  toScreenX,
  toScreenY,
  toScreenSize,
  scale,
  atlas,
  currentDocId,
  onDrillDownDoc,
}: DocOverlayProps) {
  const docRects = useMemo<DocRect[]>(() => {
    const out: DocRect[] = [];
    for (const group of groups) {
      if (!group.atlasRef) continue;
      const docId = group.atlasRef.documentId;
      const initialSheetId = group.atlasRef.initialSheetId;
      const groupBlocks = blocks.filter((b) => group.blockIds.includes(b.id));
      if (groupBlocks.length === 0) continue;

      const bounds = computeBounds(groupBlocks, blockDimensions);
      if (!bounds) continue;

      const meta = atlas?.documents.find((d) => d.id === docId);
      out.push({
        group,
        documentId: docId,
        initialSheetId,
        title: meta?.title ?? `Doc ${docId.slice(0, 6)}`,
        sheetCount: meta?.sheets.length ?? 0,
        blockCount: meta?.sheets.reduce((s, sh) => s + sh.blockCount, 0) ?? 0,
        isCurrent: currentDocId === docId,
        left: toScreenX(bounds.minX - 16) * scale,
        top: toScreenY(bounds.minY - 32) * scale,
        width: toScreenSize(bounds.maxX - bounds.minX + 32) * scale,
        height: toScreenSize(bounds.maxY - bounds.minY + 48) * scale,
      });
    }
    return out;
  }, [groups, blocks, blockDimensions, atlas, currentDocId, scale, toScreenX, toScreenY, toScreenSize]);

  if (docRects.length === 0) return null;

  return (
    <>
      {docRects.map((d) => (
        <div
          key={d.group.id}
          className={
            'absolute rounded-sm border-2 border-purple-500 dark:border-purple-400 ' +
            'bg-purple-500/15 dark:bg-purple-400/15 ' +
            'pointer-events-auto cursor-pointer ' +
            (d.isCurrent ? 'ring-2 ring-purple-500 animate-pulse-slow shadow-[0_0_0_2px_rgba(168,85,247,0.4)]' : '')
          }
          style={{
            left: d.left,
            top: d.top,
            width: d.width,
            height: d.height,
          }}
          title={`${d.title} · ${d.sheetCount} sheets · ${d.blockCount} blocks`}
          onDoubleClick={(ev) => {
            ev.stopPropagation();
            onDrillDownDoc?.(d.documentId, d.initialSheetId);
          }}
          data-fw-minimap-doc={d.documentId}
        >
          <div className="flex items-center gap-1 px-1 pt-0.5">
            <BookOpen size={8} className="shrink-0 text-purple-700 dark:text-purple-300" />
            <span className="truncate text-[8px] font-medium text-purple-900 dark:text-purple-100">
              {truncate(d.title, TITLE_MAX_CHARS)}
            </span>
          </div>
        </div>
      ))}
    </>
  );
}

interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function computeBounds(
  groupBlocks: WorkflowBlock[],
  dimensions?: Record<string, BlockDimensions>,
): Bounds | null {
  if (groupBlocks.length === 0) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const b of groupBlocks) {
    const w = dimensions?.[b.id]?.width ?? 200;
    const h = dimensions?.[b.id]?.height ?? 80;
    minX = Math.min(minX, b.x - w / 2);
    minY = Math.min(minY, b.y - h / 2);
    maxX = Math.max(maxX, b.x + w / 2);
    maxY = Math.max(maxY, b.y + h / 2);
  }
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, maxX, maxY };
}

function truncate(s: string, maxChars: number): string {
  if (s.length <= maxChars) return s;
  return s.slice(0, maxChars - 1) + '…';
}
